import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * usePageDwell — page-level dwell-time telemetry.
 *
 * Fires POST /api/analytics/dwell whenever the child leaves a page
 * (route change, tab hidden, or unload). Body:
 *   { path, ms, tier, ts }
 *
 * • navigator.sendBeacon() so the request survives a tab close.
 * • Skips runs shorter than 800 ms — kids often bounce off a tile,
 *   we don't want to flood the log with rapid pass-throughs.
 * • Skips /api/* and /admin/* on purpose — this is a UX signal for
 *   the kids' journey, not admin browsing.
 *
 * Mount ONCE at the app root (Layout.jsx). Every route change and
 * visibility change is auto-tracked.
 */
const MIN_DWELL_MS = 800;
const API = process.env.REACT_APP_BACKEND_URL || "";

export default function usePageDwell(tierProvider) {
  const location = useLocation();
  const enterAt = useRef(Date.now());
  const currentPath = useRef(location.pathname);

  useEffect(() => {
    // On path change, flush the previous dwell.
    const previous = currentPath.current;
    const previousMs = Date.now() - enterAt.current;
    if (previous && previousMs >= MIN_DWELL_MS && !previous.startsWith("/admin")) {
      sendDwell({ path: previous, ms: previousMs, tier: safeTier(tierProvider) });
    }
    currentPath.current = location.pathname;
    enterAt.current = Date.now();
  }, [location.pathname, tierProvider]);

  useEffect(() => {
    // Visibility + unload flush.
    const flush = () => {
      const ms = Date.now() - enterAt.current;
      if (ms >= MIN_DWELL_MS && !currentPath.current.startsWith("/admin")) {
        sendDwell({ path: currentPath.current, ms, tier: safeTier(tierProvider) });
      }
      enterAt.current = Date.now();
    };
    const onVis = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", flush);
    return () => {
      window.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", flush);
    };
  }, [tierProvider]);
}

function safeTier(tp) {
  try { return typeof tp === "function" ? tp() : tp; } catch { return null; }
}

function sendDwell({ path, ms, tier }) {
  if (!API) return;
  try { if (/^\/airline(\/|$)/.test(path || "") || sessionStorage.getItem("mwq_airline_session") === "1") return; } catch { /* ignore */ } // shared sessions send nothing
  const body = JSON.stringify({ path, ms: Math.round(ms), tier: tier || null, ts: Date.now() });
  try {
    // sendBeacon survives tab close; XHR/fetch may be aborted.
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(`${API}/api/analytics/dwell`, blob);
      return;
    }
    fetch(`${API}/api/analytics/dwell`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch { /* telemetry is best-effort */ }
}
